<template>
    <div class="pagination-holder">
        <nav aria-label="..." v-show="(displayPerPageInput && pages > 0) || pages > 1" class="noselect">

            <div class="block" v-if="displayPaginationSummary">
                <oc-pagination-summary
                    :page="currentPage"
                    :rows-per-page="$_rowsPerPage"
                    :total-rows="totalRows"
                    class="pagination-summary mt-2 mr-2 mb-2"
                    v-bind="paginationSummaryOptions"
                    v-class="paginationSummaryOptions.class"
                />
            </div>

            <div class="block" v-if="pages > 1">
                <ul class="pagination pagination-pager" v-class="paginClasses">
                    <li v-class="['page-item', { disabled: firstPageDisabled }]" @click="goToPage('first')" v-if="firstPageVisible">
                        <span class="page-link" :title="firstTextLabel" v-html="firstText" />
                    </li>
                    <li v-class="['page-item', { disabled: prevPageDisabled }]" @click="goToPage('prev')" v-if="prevPageVisible">
                        <span class="page-link" :title="prevTextLabel" v-html="prevText" />
                    </li>

                    <li v-for="pageNum in visiblePageNumbers" @click="goToPage(pageNum)" v-bind="activeAttrs" v-class="pageClasses(pageNum)">
                        <span class="page-link" :title="pageLabel">
                            {{pageNum}}
                            <span class="sr-only" v-if="isPageActive(pageNum)">(current)</span>
                        </span>
                    </li>

                    <li v-class="['page-item', { disabled: nextPageDisabled }]" @click="goToPage('next')" v-if="nextPageVisible">
                        <span class="page-link" :title="nextTextLabel" v-html="nextText" />
                    </li>
                    <li v-class="['page-item', { disabled: lastPageDisabled }]" @click="goToPage('last')" v-if="lastPageVisible">
                        <span class="page-link" :title="lastTextLabel" v-html="lastText" />
                    </li>
                </ul>
            </div>

            <div class="block pagination-go-to-page" v-if="displayUserInput">
                <div class="form-group">
                    <div class="input-group">
                        <oc-text-input :placeholder="userInputPlaceholder" v-model="userInputPage" @keyup.enter="changePageFromUserInput(userInputPage)" class="goto-input form-control" v-class="userInputClasses" v-bind="{disabled}" />
                        <oc-button type="secondary" @click="changePageFromUserInput(userInputPage)" :text="userInputButtonText" class="goto-btn" v-class="userInputBtnClasses" v-bind="{disabled}" />
                    </div>
                </div>
            </div>

            <div class="block pagination-per-page" v-if="displayPerPageInput">
                <div class="form-group">
                    <div class="input-group">
                        <label class="text-muted mr-2 per-page-label">{{perPageInputLabel}}</label>

                        <oc-select-input
                            :emptyRow="false"
                            :useTextAsValue="true"
                            :options="$_perPageInputOptions"
                            v-model="currentRowsPerPage"
                            :disabled="disabled"
                            v-bind="perPageInputAttrs"
                            v-class="['form-control', 'form-control-' + $_size]"
                            class="per-page-input"
                            @change="changeRowsPerPage"
                        />
                    </div>
                </div>
            </div>

        </nav>
    </div>
</template>
<script>
import {getQueryStringParam, appendQueryString} from '@/helpers/Utils'

export default {
    name: 'oc-pagination',
    model: {
        prop: 'page',
        event: 'change'
    },
    props: {
        page: {
            type: [String, Number],
            default: 1
        },
        rowsPerPage: {
            type: [String, Number],
            required: true
        },
        totalRows: {
            type: [String, Number],
            required: true
        },
        visiblePages: {
            type: [String, Number, Boolean],
            default: 8
        },
        disabled: {
            type: Boolean,
            default: false
        },
        pageParam : {
            type: String,
            default: 'page'
        },
        useRouter : {
            type: Boolean,
            default: true
        },
        // options
        align: {
            type: String,
            default: 'left',
            validator: function (value) {
                return ['left', 'center', 'right'].indexOf(value) !== -1
            }
        },
        size: {
            type: String,
            default: 'normal',
            validator: function (value) {
                return ['small', 'normal', 'big'].indexOf(value) !== -1
            }
        },
        activeAttrs: {
            type: Object,
            default: () => ({})
        },

        // per page
        perPageParam : {
            type: String,
            default: 'perPage'
        },
        displayPerPageInput: {
            type: Boolean,
            default: false
        },
        perPageInputLabel: {
            type: String,
            default: 'Per page:'
        },
        perPageInputOptions: {
            type: [Array, Object],
            default: () => [20, 50, 100, 200, 300, 400, 500]
        },
        perPageInputAttrs: {
            type: Object,
            default: () => ({})
        },

        // user input
        displayUserInput: {
            type: Boolean,
            default: true
        },
        userInputButtonText: {
            type: [String, Number],
            default: 'Go'
        },
        userInputPlaceholder: {
            type: [String, Number],
            default: 'Page #'
        },

        // first prev page next last
        displayShortcuts: {
            type: Boolean,
            default: true
        },
        hideDisabledShortcuts: {
            type: Boolean,
            default: false
        },

        firstText: {
            type: [String, Number, Boolean],
            default: '&laquo;'
        },
        firstTextLabel: {
            type: [String, Number],
            default: 'Goto first page'
        },

        prevText: {
            type: [String, Number, Boolean],
            default: '&lsaquo;'
        },
        prevTextLabel: {
            type: [String, Number],
            default: 'Goto previous page'
        },

        pageLabel: {
            type: [String, Number],
            default: 'Goto page'
        },

        nextText: {
            type: [String, Number, Boolean],
            default: '&rsaquo;'
        },
        nextTextLabel: {
            type: [String, Number],
            default: 'Goto next page'
        },

        lastText: {
            type: [String, Number, Boolean],
            default: '&raquo;'
        },
        lastTextLabel: {
            type: [String, Number],
            default: 'Goto last page'
        },

        // paging summary
        displayPaginationSummary: {
            type: Boolean,
            default: true
        },
        paginationSummaryOptions: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            currentPage: parseInt(this.page),
            currentRowsPerPage: parseInt(this.rowsPerPage),
            userInputPage: null,
            userInputHasError: false
        }
    },
    watch: {
        rowsPerPage( newValue ) {
            if( newValue !== undefined && parseInt(this.currentRowsPerPage) !== parseInt(newValue) ) {
                this.changeRowsPerPage(newValue)
            }
        },
        page( newValue ) {
            if( newValue !== undefined && parseInt(this.currentPage) !== parseInt(newValue) ) {
                this.goToPage(newValue)
            }
        },
        $route: {
            immediate: true,
            handler() {
                setTimeout(() => {
                    if( this.useRouter ) {
                        let page = getQueryStringParam.bind(this)(this.pageParam)

                        if( page !== undefined && this.currentPage !== parseInt(page) ) {
                            this.goToPage(page)
                        }

                        let perPage = getQueryStringParam.bind(this)(this.perPageParam)

                        if( inArray(perPage, this.perPageInputOptions) && perPage !== undefined && parseInt(this.currentRowsPerPage) !== parseInt(perPage) ) {
                            this.changeRowsPerPage(perPage)
                        }
                    }
                }, 0)
            }
        }
    },
    methods: {
        goToPage( page, force = false ) {
            let doNothing = false

            if( inArray(page, ['first', 'prev', 'next', 'last']) ) {
                if( this[`${page}PageDisabled`] ) {
                    doNothing = true
                } else {
                    page = this[`${page}Page`]
                }
            }

            page = parseInt(page)

            if( isNaN(page) || (this.totalRows > 0 && page > this.lastPage) || page < this.firstPage ) {
                page = this.firstPage
                this.updateRouterParam()
            }

            if( page === this.currentPage ) {
                doNothing = true
            }

            if( false === this.disabled && (false === doNothing || true === force) ) {
                this.currentPage = page
                this.userInputPage = null

                let queryPage = -1
                if( this.useRouter ) {
                    queryPage = parseInt(getQueryStringParam.bind(this)(this.pageParam))
                }

                if( page !== queryPage ) {
                    this.updateRouterParam()
                }

                this.$emit('change', this.currentPage)
            }
        },
        updateRouterParam() {
            if ( this.useRouter ) {
                appendQueryString.bind(this)({
                    [this.pageParam]: this.currentPage
                })
            }
        },
        changePageFromUserInput( page ) {
            this.userInputHasError = false

            page = parseInt(page)

            if( isNaN(page) || page > this.lastPage || page < this.firstPage ) {
                this.userInputHasError = true
                return
            }

            this.userInputPage = null

            this.goToPage(page)
        },
        changeRowsPerPage( rowsPerPage ) {
            if( this.disabled ) {
                return
            }

            rowsPerPage = parseInt(rowsPerPage)

            if( this.totalRows > 0 && this.currentPage > this.pages ) {
                this.goToPage('first')
            }

            this.currentRowsPerPage = rowsPerPage

            if ( this.useRouter ) {
                appendQueryString.bind(this)({
                    [this.perPageParam]: rowsPerPage
                })
            }

            this.$emit('change-rows-per-page', rowsPerPage)
        },
        pageClasses( pageNum ) {
            return [
                'page-item',
                {
                    active: this.isPageActive(pageNum),
                    disabled: this.disabled && !this.isPageActive(pageNum)
                },
                this.activeAttrs.class
            ]
        },
        isPageActive( pageNum ) {
            return pageNum === this.currentPage
        }
    },
    computed: {
        $_rowsPerPage() {
            return parseInt(this.currentRowsPerPage)
        },
        $_perPageInputOptions() {
            let limits = this.perPageInputOptions

            if( ! inArray(this.rowsPerPage, limits) ) {
                limits.push(parseInt(this.rowsPerPage))
                limits.sort((a, b) => a - b) // sort numbers
            }

            return limits
        },

        // options
        $_size() {
            let sizes = {
                small: 'sm',
                normal: 'md',
                big: 'lg'
            }

            return sizes[this.size]
        },
        pages() {
            let pages = Math.ceil(parseInt(this.totalRows) / parseInt(this.$_rowsPerPage))

            if( isNaN(pages) ) {
                throw new Error('[Pagination] Total pages cannot be calculated properly. Please review your "rowsPerPage" and "totalRows" parameters.')
            }

            return pages
        },
        paginClasses() {
            let classes = [
                'pagination-' + this.$_size,
            ]

            if( this.align === 'center' ) {
                classes.push('justify-content-center')
            }
            else if( this.align === 'right' ) {
                classes.push('justify-content-end')
            }

            return classes
        },
        userInputBtnClasses() {
            return 'btn-' + this.$_size
        },
        userInputClasses() {
            let classes = [
                {
                    'is-invalid': this.userInputHasError
                }
            ]

            classes.push('form-control-' + this.$_size)

            return classes
        },

        // shortcut page nums
        firstPage () {
            return 1
        },
        prevPage () {
            return this.currentPage - 1
        },
        nextPage () {
            return this.currentPage + 1
        },
        lastPage () {
            return this.pages
        },

        // visibility
        firstPageVisible () {
            return this.displayShortcuts && this.firstText !== false && ( ! this.hideDisabledShortcuts || ! this.firstPageDisabled )
        },
        prevPageVisible () {
            return this.displayShortcuts && this.prevText !== false && ( ! this.hideDisabledShortcuts || ! this.prevPageDisabled )
        },
        nextPageVisible () {
            return this.displayShortcuts && this.nextText !== false && ( ! this.hideDisabledShortcuts || ! this.nextPageDisabled )
        },
        lastPageVisible () {
            return this.displayShortcuts && this.lastText !== false && ( ! this.hideDisabledShortcuts || ! this.lastPageDisabled )
        },

        // disabled
        firstPageDisabled () {
            return this.disabled || this.currentPage === this.firstPage
        },
        prevPageDisabled () {
            return this.disabled || this.currentPage === this.firstPage
        },
        nextPageDisabled () {
            return this.disabled || this.currentPage === this.lastPage
        },
        lastPageDisabled () {
            return this.disabled || this.currentPage === this.lastPage
        },
        visiblePageNumbers () {
            let pageNums = [];
            let half = parseInt(this.visiblePages) / 2
            let left = Math.floor(half)
            let right = Math.ceil(half)

            for (let pageNum = Math.max(this.currentPage - left, this.firstPage); pageNum <= Math.min(this.currentPage + right, this.lastPage); pageNum++) {
                pageNums.push(pageNum);
            }

            return pageNums
        }
    }
}
</script>
<style scoped>
.block {
    float: left;
    margin-right: 0.5rem !important;
    margin-bottom: 1rem !important;
}

.pagination-summary {
    display: inline-block;
    white-space: nowrap;
    margin-bottom: 0 !important;
}

.pagination-go-to-page >>> .form-group,
.pagination-per-page >>> .form-group {
    margin-bottom: 0;
}

.per-page-label {
    padding-top: 6px;
}

.pagination-go-to-page >>> .goto-input {
    width: 80px;
    margin: 0 3px 0 0;
}

ul.pagination {
    margin-bottom: 0;
}

ul.pagination li {
    cursor: pointer;
}
ul.pagination li.disabled {
    cursor: default;
}

.form-control-lg.goto-input {
    height: 3.5rem;
}

.form-control-lg.per-page-input {
    height: 3.5rem;
}
.noselect {
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
    -khtml-user-select: none; /* Konqueror HTML */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none; /* Non-prefixed version, currently
    supported by Chrome and Opera */
}
</style>
